import type React from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Card, Chip } from '@heroui/react';
import { PressableFeedback } from '@heroui-pro/react';
import { countParam } from '@/pages/catalogImport/notices';
import { formatGel } from '@/utils/money';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { RowDrawerCandidate } from './rowDrawerResolutions';

type RowDrawerCandidatesProps = {
    candidates: RowDrawerCandidate[];
    onPick: (variantId: string) => void;
};

/** "SKU HC-20W · Barcode 6931474712345 · 128 GB · Black · Stock 18 · ₾ 29.00", leaving out what is unknown. */
const detailsOf = (t: TFunction, candidate: RowDrawerCandidate): string =>
    [
        candidate.sku === null
            ? null
            : t('catalogImport.products.drawer.sku', { sku: candidate.sku }),
        candidate.barcode === null
            ? null
            : t('catalogImport.products.drawer.candidates.barcode', {
                  barcode: candidate.barcode,
              }),
        ...candidate.attributes,
        t('catalogImport.products.drawer.candidates.stock', {
            stock: countParam(candidate.stock),
        }),
        candidate.salePriceTetri === null
            ? null
            : formatGel(candidate.salePriceTetri),
    ]
        .filter((part): part is string => part !== null && part !== '')
        .join(' · ');

const MATCHED_BY_KEY = {
    sku: 'catalogImport.products.drawer.candidates.matchedBySku',
    barcode: 'catalogImport.products.drawer.candidates.matchedByBarcode',
} as const;

/**
 * The catalog variants an ambiguous row could mean, inside the problem card:
 * one sentence on what a pick does, then a pressable card per variant (a
 * native button drawn as a card, as the Welcome step's choices: Tab reaches
 * it, Enter or Space picks it). A card names the product, then its SKU where
 * known, barcode, values, stock in the branch and sale price; on a
 * SKU-and-barcode conflict a chip says which of the two it shares with the
 * row. A button holds phrasing content only, hence spans.
 */
export const RowDrawerCandidates: React.FC<RowDrawerCandidatesProps> = ({
    candidates,
    onPick,
}) => {
    const { t } = useTranslation('pages');

    return (
        <div
            className="mt-2 flex w-full flex-col gap-2"
            {...getNodeTestIdProp('catalog-import-row-drawer-candidates')}
        >
            <p className="text-sm">
                {t('catalogImport.products.drawer.candidates.intro')}
            </p>
            <ul className="flex flex-col gap-2">
                {candidates.map((candidate) => (
                    <li key={candidate.variantId}>
                        <Card<'button'>
                            className="relative w-full cursor-pointer gap-1 overflow-hidden p-3 text-start"
                            onClick={() => onPick(candidate.variantId)}
                            render={(props) => (
                                <button type="button" {...props} />
                            )}
                            variant="secondary"
                            {...getNodeTestIdProp(
                                `catalog-import-row-drawer-candidate-${candidate.variantId}`,
                            )}
                        >
                            <PressableFeedback.Highlight />
                            <span className="flex flex-wrap items-center gap-2">
                                <Card.Title
                                    render={(props) => <span {...props} />}
                                >
                                    {candidate.productName}
                                </Card.Title>
                                {candidate.matchedBy === null ? null : (
                                    <Chip size="sm" variant="soft">
                                        {t(MATCHED_BY_KEY[candidate.matchedBy])}
                                    </Chip>
                                )}
                            </span>
                            <Card.Description
                                render={(props) => <span {...props} />}
                            >
                                {detailsOf(t, candidate)}
                            </Card.Description>
                        </Card>
                    </li>
                ))}
            </ul>
        </div>
    );
};

/** The picked candidate's one line, for the pick card. */
export const candidateSummaryOf = (
    t: TFunction,
    candidate: RowDrawerCandidate,
): string => `${candidate.productName} · ${detailsOf(t, candidate)}`;
